import { useQuery } from "@tanstack/react-query";
import { getBusinessCustomSlotsV2 } from "apis";
import dateFormat from "dateformat";
import useQueueStore from "../../stores/queue-store";
import dayjs from "dayjs";
import { useCreateAptStore } from "@elixir/stores";
import { useMyAccessProfileV2 } from "./get-my-access-profile";
import { getCalendarConfigs } from "../../utils/bridges/methods";
import { MODES_OF_APT } from "../../stores/queue-store/enums";
import { getShouldShowCalendarSlots } from "../../utils/get-calendar-slot-mode";

export const getCustomSlotsQuery = (
  slotDateString: string,
  customSlotDuration: number,
  clinicId: string,
  docId: string,
  mode?: "tele" | "in-clinic"
) => ["slots", slotDateString, customSlotDuration, clinicId, docId, mode];

export const getCustomSlots = async (
  slotDateString: string,
  businessId: string,
  customSlotDuration: number,
  clinicIdForSlots: string,
  doctorId: string,
  mode?: MODES_OF_APT,
  forceShowSlotsFromSchedule?: boolean
) => {
  const { slotsFromDoctorCalendar } = await getCalendarConfigs();

  const useCalendarSlots =
    getShouldShowCalendarSlots(slotsFromDoctorCalendar) ||
    !!forceShowSlotsFromSchedule;

  const { startTime, endTime } = getSlotStartAndEndTime({
    slotDateString,
    businessId,
    showSlotsFromDoctorCalendar: useCalendarSlots,
  });

  const { slots, services } = await getBusinessCustomSlotsV2({
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
    slotDuration: customSlotDuration,
    clinicId: clinicIdForSlots,
    docId: doctorId,
    slotsFromCalendar: useCalendarSlots,
    mode: mode === MODES_OF_APT.TELE ? "tele" : "in-clinic",
  });

  return {
    slotsByDate: {
      [slotDateString]: useCalendarSlots
        ? slots.filter((item) => item.in_schedule)
        : slots,
    },
    services: useCalendarSlots ? services : undefined,
  };
};

/**
 * This function will pick customSlotDate, customSlotDuration and Get start time, end time based on time of day selected from queueStore in zustand and give you the react query response.
 */
export function useCustomSlots({
  clinicIdToFetch,
  docId,
  forceShowSlotsFromSchedule = false,
}: {
  clinicIdToFetch?: string;
  docId?: string;
  forceShowSlotsFromSchedule?: boolean;
} = {}) {
  const customSlotDate = useQueueStore((state) => state.customSlotDate);
  const customSlotDuration = useQueueStore((state) => state.customSlotDuration);
  const fallbackDoctorId = useCreateAptStore(
    (state) => state.selectedDoctor?.oid
  );
  const fallbackClinicId = useCreateAptStore((state) => state.selectedClinic);

  const doctorId =
    docId || fallbackDoctorId || `c-${clinicIdToFetch || fallbackClinicId}`;

  const clinicId = useQueueStore((state) => state.clinicId);
  const slotDateString = dateFormat(customSlotDate, "yyyy-mm-dd");

  const { data: myAccessProfile } = useMyAccessProfileV2();
  const businessId = myAccessProfile?.business?.id!;

  const clinicIdForSlots = clinicIdToFetch || fallbackClinicId || clinicId;

  const mode = useQueueStore((state) => state.mode);

  return useQuery(
    getCustomSlotsQuery(
      slotDateString,
      customSlotDuration,
      clinicIdForSlots,
      doctorId,
      mode === MODES_OF_APT.TELE ? "tele" : "in-clinic"
    ),
    () =>
      getCustomSlots(
        slotDateString,
        businessId,
        customSlotDuration,
        clinicIdForSlots,
        doctorId,
        mode,
        forceShowSlotsFromSchedule
      ),
    {
      retry: false,
      enabled: (!!clinicIdToFetch || !!clinicId) && !!doctorId,
    }
  );
}

function getSlotStartAndEndTime({
  slotDateString,
  businessId,
  showSlotsFromDoctorCalendar = false,
}: {
  slotDateString: string;
  businessId?: string;
  showSlotsFromDoctorCalendar?: boolean;
}) {
  let startTime;
  let endTime;

  if (showSlotsFromDoctorCalendar || businessId === "75093850685457") {
    startTime = dayjs(slotDateString).startOf("day");
    endTime = dayjs(slotDateString).endOf("day");
  } else if (businessId === "74117920305567") {
    startTime = dayjs(slotDateString).startOf("day").set("hour", 9);
    endTime = dayjs(slotDateString).startOf("day").set("hour", 19);
  } else {
    startTime = dayjs(slotDateString).startOf("day").set("hour", 8);
    endTime = dayjs(slotDateString).startOf("day").add(1, "day");
  }
  return { startTime, endTime };
}
